import { Store } from 'redux';
import { App } from '@ps20-frontend/frontend-models';
import { MigrationErrorName } from '@ps20-frontend/migrations';
import { GlobalAction, GlobalActions } from './global-actions';
import { TaasShopGlobalActions } from './taasShopGlobalActions';
import { AngebotsId } from './payload-models';

export interface StoreRef {
  appName: string;
  angebotsId?: string;
  store: Store;
}

export class GlobalEventDispatcher {
  private static instance: GlobalEventDispatcher;
  stores: Array<StoreRef>;
  removedStores: Array<StoreRef>;

  static getInstance(): GlobalEventDispatcher {
    if (!this.instance) {
      this.instance = new GlobalEventDispatcher();
    }

    return this.instance;
  }

  static _reset(): void {
    this.instance = new GlobalEventDispatcher();
  }

  private static getMessageForLogger(storeRef: StoreRef, actionType: string): string {
    return `globalEventDispatcher.dispatch: exception in store ${storeRef.appName} during event ${actionType}`;
  }

  private constructor() {
    this.stores = [];
    this.removedStores = [];
  }

  addStore(app: App | string, storeApp: Store, angebotsId?: string): void {
    if (typeof storeApp['dispatch'] !== 'function') {
      throw new Error('Store should contain a dispatch method!');
    }
    if (typeof storeApp['getState'] !== 'function') {
      throw new Error('Store should contain a getState method!');
    }
    const storeRef: StoreRef = {
      appName: app,
      store: storeApp,
      angebotsId,
    };
    this.stores.push(storeRef);
  }

  findStoreRef(appName: App | string, angebotsId?: string): StoreRef | undefined {
    return this.stores.find(
      storeRef => storeRef.appName === appName && storeRef.angebotsId === angebotsId,
    );
  }

  nextStoreIndexFor(appId: App): number {
    return this.stores.filter(storeRef => storeRef.appName === appId).length;
  }

  reactivateRemovedStore(appId: App): StoreRef {
    const removedStore = this.removedStores.find(storeRef => storeRef.appName === appId);
    if (removedStore) {
      this.removedStores = this.removedStores.filter(
        removedStoreRef => removedStoreRef !== removedStore,
      );
    }

    return removedStore;
  }

  removeStore(angebotsId: AngebotsId): void {
    const storeToRemove = this.stores.find(
      store => store.appName === angebotsId.appId && store.angebotsId === angebotsId.angebotsId,
    );
    if (storeToRemove) {
      this.removedStores = [...this.removedStores, storeToRemove];
      this.stores = this.stores.filter(
        store => store.appName !== angebotsId.appId || store.angebotsId !== angebotsId.angebotsId,
      );
    }
  }

  dispatch(event: GlobalAction<unknown>): void {
    if (TaasShopGlobalActions.includes(event.type)) {
      return this.dispatch(new GlobalActions().createStateSnapshotForAllApps(event));
    }
    if (event.type === GlobalActions.STATE_SNAPSHOT_FOR_ALL_APPS_CREATED) {
      event = event.payload as GlobalAction<unknown>;
    }

    this.stores.forEach(storeRef => {
      try {
        storeRef.store.dispatch(event);
      } catch (e) {
        const errorMessage = GlobalEventDispatcher.getMessageForLogger(storeRef, event.type);
        this.handleDispatchError(e, errorMessage);
      }
    });
  }

  handleDispatchError(e: unknown, errorMessage: string): void {
    this.dispatch(new GlobalActions().logError(App.SHOP, e as string, errorMessage));
    if ((e as Error)?.name === MigrationErrorName) {
      return this.dispatch(new GlobalActions().errorPageRequested());
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getState(): any {
    const state: unknown[] = [];
    this.stores.forEach(storeRef => state.push(storeRef.store.getState()));
    return state;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getStateByAngebot(appName: App, angebotsId?: string): any {
    try {
      return this.stores
        .find(storeRef => storeRef.appName === appName && storeRef.angebotsId === angebotsId)
        .store.getState();
    } catch {
      throw new Error(`Store >>${appName}<< >>${angebotsId}<< not found!`);
    }
  }
}
